import React from 'react';
import * as Toast from '@radix-ui/react-toast';
import { CheckCircle, XCircle, AlertCircle, Info } from 'lucide-react';

export interface ToastProps {
  id: string;
  title: string;
  description?: string;
  type: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
  onClose: (id: string) => void;
}

export const ToastItem: React.FC<ToastProps> = ({
  id,
  title,
  description,
  type,
  duration = 5000,
  onClose,
}) => {
  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-success-600" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-error-600" />;
      case 'warning':
        return <AlertCircle className="h-5 w-5 text-warning-600" />;
      case 'info':
        return <Info className="h-5 w-5 text-primary-600" />;
    }
  };

  const getColorClasses = () => {
    switch (type) {
      case 'success':
        return 'border-success-200 bg-success-50 dark:border-success-800 dark:bg-success-900/20';
      case 'error':
        return 'border-error-200 bg-error-50 dark:border-error-800 dark:bg-error-900/20';
      case 'warning':
        return 'border-warning-200 bg-warning-50 dark:border-warning-800 dark:bg-warning-900/20';
      case 'info':
        return 'border-primary-200 bg-primary-50 dark:border-primary-800 dark:bg-primary-900/20';
    }
  };

  return (
    <Toast.Root
      className={`${getColorClasses()} border rounded-lg shadow-lg p-4 max-w-sm w-full data-[state=open]:animate-slideIn data-[state=closed]:animate-hide data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=cancel]:translate-x-0 data-[swipe=cancel]:transition-[transform_200ms_ease-out] data-[swipe=end]:animate-swipeOut`}
      duration={duration}
      onOpenChange={(open) => !open && onClose(id)}
    >
      <div className="flex items-start gap-3">
        {getIcon()}
        <div className="flex-1">
          <Toast.Title className="text-sm font-medium text-secondary-900 dark:text-white">
            {title}
          </Toast.Title>
          {description && (
            <Toast.Description className="text-sm text-secondary-600 dark:text-secondary-300 mt-1">
              {description}
            </Toast.Description>
          )}
        </div>
        <Toast.Close
          className="text-secondary-400 hover:text-secondary-600 dark:text-secondary-500 dark:hover:text-secondary-300"
          aria-label="Close"
        >
          <XCircle className="h-4 w-4" />
        </Toast.Close>
      </div>
    </Toast.Root>
  );
};

export const ToastViewport: React.FC = () => (
  <Toast.Viewport className="fixed bottom-0 right-0 flex flex-col p-6 gap-2 w-96 max-w-[100vw] m-0 list-none z-[2147483647] outline-none" />
);